import { isAddress } from 'ethers'

/**
 * DCA deployment of this build, read from `.env` (babel-inlined):
 *
 * - `DCA_API_URL`: the ZKX Trade host that serves `/dca/*`. Its own key rather
 *   than `ZKX_TRADE_API_URL`, because DCA ships on the dev host first while
 *   swaps already run against production.
 * - `DCA_EXECUTOR_ADDRESS`: the `DcaExecutor` contract this build trusts. Pinned
 *   in the binary on purpose (§4.1 of the DCA client doc): the address returned
 *   by `GET /dca/config` is only accepted when it matches this one, so a
 *   compromised API cannot point the user's approval at another spender.
 *
 * DCA is OFF unless both are set. `scripts/validateEnv.js` refuses a build that
 * sets only one of them.
 */
// Same shape as ZKX_TRADE_API_URL: the gateway adds `/v1`, so a base that already
// ends in it would request `/trade/v1/v1/dca/*` (see Trello #571).
const normalizeBase = (url: string | undefined) =>
  (url || '').trim().replace(/\/+$/, '').replace(/\/v1$/i, '')

const DCA_API_BASE = normalizeBase(process.env.DCA_API_URL)

const rawExecutor = (process.env.DCA_EXECUTOR_ADDRESS || '').trim()
const DCA_EXECUTOR = isAddress(rawExecutor) ? rawExecutor.toLowerCase() : ''

/**
 * The chain and the wrapped native token DCA runs on. Canonical PulseChain
 * values, not per-environment, so they are constants rather than env keys —
 * but pinned for the same reason as the executor: the wrap step SENDS PLS to
 * this address, so it must never come from the API.
 */
export const DCA_CHAIN_ID = 369
export const DCA_WRAPPED_NATIVE = '0xa1077a294dde1b09bb078844df40758a5d0f9a27'

export const isDcaConfigured = (): boolean => !!DCA_API_BASE && !!DCA_EXECUTOR

export const getDcaApiBase = (): string => {
  if (!DCA_API_BASE) throw new Error('DCA API base is not configured — set DCA_API_URL in .env')
  return DCA_API_BASE
}

export const getPinnedDcaExecutor = (): string => {
  if (!DCA_EXECUTOR)
    throw new Error('DCA executor is not configured — set DCA_EXECUTOR_ADDRESS in .env')
  return DCA_EXECUTOR
}
